import React, { useEffect, useState } from 'react';
import { clsx } from 'clsx';

import type { TagEntity } from '@schibsted/frapi-schema';
import type {
  ByfunnFrapiArticle,
  ByfunnPromoArticle,
  TagType,
} from '../../../../../types/frapi-types.js';
import type { SaveListItem } from '../../../../../types/save-types.js';

import { usePulseContext } from '../../../../../../core/mandatory-integrations/pulse/PulseProvider.js';
import { useLocalStorage } from '../../../../hooks/useLocalStorage.js';
import { useListsContext } from '../../../saving/ListsProvider.js';
import { itemIsSaved } from '../../../saving/utils.js';
import TeaserGrade from '../../../teasers/teaserComponents/TeaserGrade.js';
import { filterTagsOnType } from '../../helpers.js';
import Price from '.././Price.js';
import SaveArticleReminder from '.././SaveArticleReminder.js';
import SaveButton from '.././SaveButton.js';
import Tags from '.././Tags.js';
import UrlButtons from './UrlButtons.js';

export type AboutProps = {
  article: ByfunnFrapiArticle | ByfunnPromoArticle;
  handleOverlay: (item: SaveListItem) => void;
  item: SaveListItem;
  isMobile?: boolean;
  isRestaurantReview?: boolean;
  enableRuterCampaign: boolean;
};

export const getLocationTag = (tags: TagEntity[]) => {
  const tagsArray = tags.filter((tag) => tag.title.startsWith('Vink/område'));
  const tagTitle = tagsArray[0]?.title.split('/')[2];
  const tagSlug = '/tag/' + tagsArray[0]?.slug;

  return tagsArray.length > 0 ? <a href={tagSlug}>, {tagTitle}</a> : '';
};

const About: React.FC<AboutProps> = ({
  article,
  handleOverlay,
  item,
  isMobile,
  isRestaurantReview = true,
  enableRuterCampaign,
}) => {
  const { tags } = article;
  const { trackClickUIElement } = usePulseContext();
  const { savedItems, isLoading } = useListsContext();
  const [isSaved, setIsSaved] = useState(false);
  const [showSaveReminder, setShowSaveReminder] = useLocalStorage(
    'showSaveReminder',
    true,
  );

  const isRuterCampaignActive = enableRuterCampaign && !!article.ruterUrl;

  useEffect(() => {
    if (savedItems.length > 0) {
      setIsSaved(itemIsSaved(item, savedItems));
    }
  }, [item, savedItems]);

  const handleHideReminder = () => {
    trackClickUIElement({
      object: {
        id: `article:hide-save-reminder`,
        name: 'Hide save reminder',
        elementType: 'Button',
      },
    });
    setShowSaveReminder(false);
  };

  const handleOnClick = () => {
    trackClickUIElement({
      object: {
        id: 'article:save-button',
        name: `Save ${isRestaurantReview ? 'restaurant' : 'movie'} article`,
        elementType: 'Button',
      },
    }).finally(() => {
      handleOverlay(item);
      if (showSaveReminder) {
        setShowSaveReminder(false);
      }
    });
  };

  const shouldRenderSaveReminder = showSaveReminder && !isSaved && !isLoading;

  const containsTagOfType = (type: TagType) =>
    filterTagsOnType(tags, type).length > 0;

  const Buttons = ({ children }: { children?: React.ReactNode }) => (
    <div
      className={clsx('buttons', {
        'with-ruter-button': isRuterCampaignActive,
      })}
    >
      {children}
      <div className="save-container">
        <SaveButton
          handleOnClick={handleOnClick}
          item={item}
          isSaved={isSaved}
          disabled={isLoading}
          condensed={isRuterCampaignActive && !!isMobile}
        />
        {shouldRenderSaveReminder && (
          <SaveArticleReminder handleClose={handleHideReminder} />
        )}
      </div>
    </div>
  );

  const RestaurantInfo = () => (
    <div className="about">
      <div className="header-container">
        <h2 className="title">{article?.restaurantTitle}</h2>
        {article.grade && <TeaserGrade grade={article.grade} />}
      </div>
      <div className="tag-container">
        {article.priceLevel && article.priceLevel !== 'none' && (
          <Price price={article.priceLevel} />
        )}
        {containsTagOfType('kjøkken') &&
          article.priceLevel &&
          article.priceLevel !== 'none' && <div className="tag-separator" />}
        <Tags tags={tags} type={'kjøkken'} limit={2} noBorder />
        {containsTagOfType('område') && <div className="tag-separator" />}
        <Tags tags={tags} type={'område'} noBorder />
      </div>
      <Buttons>
        <UrlButtons
          article={article}
          compact={!!isMobile}
          renderRuterButton={isRuterCampaignActive}
        />
      </Buttons>
    </div>
  );

  const MovieAbout = () => (
    <div className="about movie">
      <div className="header-container">
        <h2 className="title">{article?.restaurantTitle}</h2>
        {article.grade && <TeaserGrade grade={article.grade} />}
      </div>
      <div className="tag-container">
        <Tags tags={tags} type="strømmetjeneste" noBorder />
        <div className="tag-separator"></div>
        <Tags tags={tags} type={['format']} noBorder regular />
        <div className="tag-separator"></div>
        <Tags tags={tags} type={'type'} limit={2} noBorder regular />
      </div>
      <Buttons />
    </div>
  );

  return isRestaurantReview ? <RestaurantInfo /> : <MovieAbout />;
};

export default About;
